import { Reveal, PageHero, SectionHead, CTABand } from "@/components/Shared";
import { approachSteps, informationWeWorkWith, principles } from "@/data/content";

export default function Approach() {
    return (
        <div data-testid="approach-page">
            <PageHero
                index="05"
                overline="Approach"
                title="From question to conclusion."
                lead="Every investigation is different. Our methodology is adapted to the allegation, available evidence, business environment and investigative objective."
                testId="approach-hero"
            />

            {/* INFORMATION WE WORK WITH */}
            <section className="blueprint-grid relative overflow-hidden" data-testid="information-section">
                <div className="ambient-glow -left-40 -top-24 h-[420px] w-[420px] bg-[#1D4F9C]/25" aria-hidden />
                <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28">
                <SectionHead
                    index="01"
                    overline="Information We Work With"
                    title="Financial data. Digital evidence. Relationships."
                    testId="information"
                />
                <div className="mt-14 grid gap-px border border-[#243650] bg-[#243650] lg:grid-cols-3">
                    {informationWeWorkWith.map((g, i) => (
                        <Reveal key={g.title} delay={i * 0.08}>
                            <div className="h-full bg-[#0F1B2E] p-9" data-testid={`info-group-${i}`}>
                                <p className="font-tech text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D9A84E]">
                                    {g.title}
                                </p>
                                <ul className="mt-6 space-y-3">
                                    {g.items.map((item) => (
                                        <li key={item} className="flex items-center gap-3 text-sm text-[#9AA7BF]">
                                            <span className="h-1.5 w-1.5 shrink-0 bg-[#1D4F9C]" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                    ))}
                </div>
                </div>
            </section>

            {/* 6-STEP METHODOLOGY */}
            <section className="border-y border-[#243650] bg-[#0F1B2E]" data-testid="methodology-section">
                <div className="mx-auto max-w-5xl px-6 py-24 lg:px-10 lg:py-28">
                    <SectionHead
                        index="02"
                        overline="Methodology"
                        title="Analyse. Trace. Corroborate. Report."
                        testId="methodology"
                    />
                    <div className="relative mt-14">
                        <div className="absolute bottom-8 left-[34px] top-8 hidden w-px bg-[#243650] sm:block" />
                        <div className="space-y-6">
                            {approachSteps.map((step, i) => (
                                <Reveal key={step.number} delay={i * 0.04}>
                                    <div
                                        className="card-lift group relative border border-[#243650] bg-[#0F1B2E] p-8 sm:ml-24 sm:flex sm:items-start sm:gap-10"
                                        data-testid={`methodology-step-${step.number}`}
                                    >
                                        <div className="absolute -left-24 top-1/2 hidden h-[70px] w-[70px] -translate-y-1/2 items-center justify-center border border-[#243650] bg-[#0B1322] font-tech text-sm tracking-[0.16em] text-[#5B93E8] transition-colors duration-300 group-hover:border-[#D9A84E] group-hover:text-[#D9A84E] sm:flex">
                                            {step.number}
                                        </div>
                                        <div className="sm:w-44 sm:shrink-0">
                                            <p className="font-tech text-[10px] tracking-[0.3em] text-[#D9A84E] sm:hidden">
                                                PHASE {step.number}
                                            </p>
                                            <h2 className="mt-2 font-display text-2xl font-semibold text-[#E8ECF4] sm:mt-0">
                                                {step.title}
                                            </h2>
                                        </div>
                                        <p className="mt-3 max-w-xl text-base leading-relaxed text-[#9AA7BF] sm:mt-0">
                                            {step.description}
                                        </p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                    <Reveal className="mt-14">
                        <p
                            className="border-l-[3px] border-[#D9A84E] pl-6 font-display text-xl font-medium text-[#E8ECF4] sm:text-2xl"
                            data-testid="methodology-doctrine"
                        >
                            Turn information into intelligence.{" "}
                            <span className="italic text-[#5B93E8]">Connect intelligence to evidence.</span>
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* INVESTIGATIVE PRINCIPLES */}
            <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28" data-testid="principles-section">
                <SectionHead
                    index="03"
                    overline="Investigative Principles"
                    title="Principles that govern every engagement."
                    testId="principles"
                />
                <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {principles.map((p, i) => (
                        <Reveal key={p.title} delay={i * 0.06}>
                            <div
                                className="card-lift group h-full border border-[#243650] bg-[#0F1B2E] p-8"
                                data-testid={`principle-${i}`}
                            >
                                <div className="inline-flex border border-[#5B93E8]/30 bg-[#16263F] p-3">
                                    <p.icon className="h-5 w-5 text-[#5B93E8]" />
                                </div>
                                <h3 className="mt-5 font-display text-lg font-semibold text-[#E8ECF4]">
                                    {p.title}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-[#9AA7BF]">
                                    {p.description}
                                </p>
                                <div className="mt-6 h-0.5 w-10 bg-[#243650] transition-all duration-500 group-hover:w-16 group-hover:bg-[#A87B2F]" />
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <CTABand
                title="Start with the Question."
                lead="Every investigation begins with understanding the situation, the available evidence and the questions that need to be answered."
                testId="approach-cta"
            />
        </div>
    );
}
